import React from 'react';
import { Filter, CheckCircle, ArrowRight, AlertTriangle, Target } from 'lucide-react';
import ModernDocHeader from '../../components/documentation/ModernDocHeader';

const FunnelManagementDocs: React.FC = () => {
  return (
    <div>
      <ModernDocHeader
        title="Funnel Management"
        description="Create custom sales funnel stages and map lead statuses to visualize and track your sales process from initial contact to closed deals."
        icon={<Filter className="w-6 h-6" />}
        badge="Essential"
        badgeColor="blue"
      />

      <div className="p-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-4xl">
          <div className="prose prose-blue dark:prose-invert max-w-none">

      {/* What You'll Learn */}
      <div className="not-prose bg-green-50 dark:bg-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Target className="w-5 h-5 text-brandColor dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">
              What You'll Learn
            </h3>
            <ul className="space-y-1 text-sm text-green-800 dark:text-green-300">
              <li>✓ Creating and customizing funnel stages</li>
              <li>✓ Mapping lead statuses to funnel stages</li>
              <li>✓ Organizing sales process with visual stages</li>
              <li>✓ Analyzing funnel conversion and performance</li>
              <li>✓ Managing stage colors and ordering</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>What is Funnel Management?</h2>
      <p>
        Funnel Management allows you to create custom stages that represent your unique sales process.
        Unlike the pipeline which uses predefined stages, funnel stages are fully customizable and
        can be mapped to specific lead statuses for advanced reporting and analytics.
      </p>

      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
        <div className="p-4 bg-green-50 dark:bg-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <h4 className="font-semibold text-green-900 dark:text-green-200 mb-2">Pipeline vs Funnel</h4>
          <p className="text-sm text-brandColor dark:text-green-300">
            <strong>Pipeline:</strong> Visual Kanban board for day-to-day lead management with
            drag-and-drop functionality.
          </p>
        </div>
        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
          <h4 className="font-semibold text-purple-900 dark:text-purple-200 mb-2">Funnel Management</h4>
          <p className="text-sm text-purple-700 dark:text-purple-300">
            <strong>Funnel:</strong> Customizable stages with status mapping for advanced analytics,
            reporting, and conversion tracking.
          </p>
        </div>
      </div>

      <h2>Accessing Funnel Management</h2>
      <p>
        Navigate to <code>Settings → Pipeline & Funnel → Funnel Management</code>
      </p>

      <h2>Creating a Funnel Stage</h2>

      <ol>
        <li>Click "Add Stage" button in the top right</li>
        <li>Fill in stage details:
          <ul>
            <li><strong>Stage Name</strong> - Descriptive name (e.g., "Initial Contact", "Demo Completed")</li>
            <li><strong>Stage Description</strong> - Detailed explanation of what this stage represents</li>
            <li><strong>Stage Order</strong> - Numeric position in funnel (lower numbers appear first)</li>
            <li><strong>Color Code</strong> - Hex color for visual identification (e.g., #3498db)</li>
          </ul>
        </li>
        <li>Click "Save" to create the stage</li>
      </ol>

      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-6">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <strong>💡 Naming Convention:</strong>
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Use action-oriented or milestone-based names that clearly indicate progress. Examples:
          "Qualified Lead", "Proposal Sent", "Contract Signed".
        </p>
      </div>

      <h2>Status Mapping</h2>

      <p>
        Status mapping connects lead statuses (dropdown values in lead forms) to your funnel stages.
        This enables powerful reporting and analytics by grouping related statuses.
      </p>

      <h3>Why Map Statuses to Stages?</h3>
      <ul>
        <li><strong>Flexible Categorization</strong> - Multiple statuses can belong to one stage</li>
        <li><strong>Better Analytics</strong> - Track conversion rates between meaningful stages</li>
        <li><strong>Cleaner Reports</strong> - Combine similar statuses for high-level insights</li>
        <li><strong>Historical Tracking</strong> - Maintain granular status data while grouping for analysis</li>
      </ul>

      <h3>How to Map Statuses:</h3>
      <ol>
        <li>Click on a funnel stage in the table</li>
        <li>Scroll to "Status Mappings" section</li>
        <li>Click "Add Status Mapping"</li>
        <li>Select status from dropdown (e.g., "Contacted", "Follow-up Scheduled")</li>
        <li>Set mapping priority (optional - for conflicting mappings)</li>
        <li>Click "Map Status"</li>
        <li>Repeat for all related statuses</li>
      </ol>

      <div className="not-prose bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden my-6">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Funnel Stage</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Mapped Statuses</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            <tr>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">New Lead</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                New, Fresh Lead, Uncontacted
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Engaged</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                Contacted, Follow-up Scheduled, In Discussion
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Qualified</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                Qualified, Budget Confirmed, Decision Maker Identified
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Proposal</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                Proposal Sent, Quote Provided, Under Review
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>Managing Funnel Stages</h2>

      <h3>Editing a Stage</h3>
      <ol>
        <li>Click the edit icon next to the stage</li>
        <li>Modify stage properties (name, description, color, order)</li>
        <li>Update status mappings if needed</li>
        <li>Click "Save Changes"</li>
      </ol>

      <h3>Deleting a Stage</h3>
      <div className="not-prose bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 my-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-2">
              <strong>⚠️ Important:</strong>
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              You cannot delete the last funnel stage. At least one stage must exist. Deleting a
              stage will remove all its status mappings, but historical lead data remains intact.
            </p>
          </div>
        </div>
      </div>

      <h3>Reordering Stages</h3>
      <p>
        Change the <strong>Stage Order</strong> field when editing a stage. Lower numbers appear
        first in reports and analytics. Stages are automatically sorted by this order value.
      </p>

      <h2>Funnel Analytics & Reports</h2>

      <h3>Conversion Tracking</h3>
      <p>
        Once statuses are mapped to stages, you can track:
      </p>
      <ul>
        <li>Lead distribution across funnel stages</li>
        <li>Conversion rates between stages</li>
        <li>Average time spent in each stage</li>
        <li>Drop-off points in the sales process</li>
        <li>Stage-based revenue forecasting</li>
      </ul>

      <h3>Generating Funnel Reports</h3>
      <p>
        Use the Funnel Report API endpoint to generate detailed analytics:
      </p>
      <ul>
        <li>Filter by date range</li>
        <li>Select specific stages to analyze</li>
        <li>Export data for external analysis</li>
        <li>Track performance trends over time</li>
      </ul>

      <h2>Best Practices</h2>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 my-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">
              Funnel Setup Checklist
            </h3>
            <ul className="space-y-2 text-sm text-green-800 dark:text-green-300">
              <li>✓ <strong>Keep it simple</strong> - 4-7 stages is optimal for most businesses</li>
              <li>✓ <strong>Clear progression</strong> - Each stage should represent clear advancement</li>
              <li>✓ <strong>Map all statuses</strong> - Ensure every status belongs to a stage</li>
              <li>✓ <strong>Use descriptive names</strong> - Stage names should be self-explanatory</li>
              <li>✓ <strong>Consistent colors</strong> - Use color scheme that makes sense (green for won, etc.)</li>
              <li>✓ <strong>Regular review</strong> - Revisit and refine stages quarterly</li>
            </ul>
          </div>
        </div>
      </div>

      <h3>Common Funnel Structures</h3>

      <div className="not-prose space-y-4 my-6">
        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <summary className="p-4 font-medium text-gray-900 dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
            B2B Sales Funnel (7 stages)
          </summary>
          <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <p>1. Lead Capture → 2. Initial Contact → 3. Qualification → 4. Needs Analysis →
               5. Proposal → 6. Negotiation → 7. Closed Won/Lost</p>
          </div>
        </details>

        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <summary className="p-4 font-medium text-gray-900 dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
            B2C E-commerce Funnel (5 stages)
          </summary>
          <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <p>1. Awareness → 2. Interest → 3. Consideration → 4. Purchase → 5. Loyalty</p>
          </div>
        </details>

        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <summary className="p-4 font-medium text-gray-900 dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
            SaaS Trial Funnel (6 stages)
          </summary>
          <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <p>1. Sign Up → 2. Onboarding → 3. Active Trial → 4. Engaged User →
               5. Conversion Discussion → 6. Paid Customer</p>
          </div>
        </details>
      </div>

      <h2>Troubleshooting</h2>

      <div className="not-prose bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 my-4">
        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Status already mapped to another stage?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p>Each status can only be mapped to one stage. Remove it from the current stage before
               mapping to a new one. Check "Status Mappings" in other stages to find where it's currently mapped.</p>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Can't find certain statuses in dropdown?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p>Only active statuses that aren't already mapped appear in the dropdown. If a status
               is missing, it's either inactive in Form Fields settings or already mapped to another stage.</p>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Reports showing incorrect stage data?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p>Funnel reports are based on current status mappings. If you recently changed mappings,
               historical data will reflect new stage groupings. This is by design - reports always
               use current configuration for consistency.</p>
          </div>
        </details>
      </div>

      <h2>Next Steps</h2>

      <div className="not-prose space-y-3 mt-6">
        <a
          href="/docs/pipeline"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Master Pipeline Management</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Learn to use visual Kanban board for day-to-day lead management
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/form-fields"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Configure Form Fields</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Customize lead form fields including status dropdowns
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>
      </div>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
              Funnel Configured Successfully!
            </h3>
            <p className="text-green-800 dark:text-green-300">
              Your customized sales funnel is now set up. Use status mappings to group related
              statuses and generate meaningful analytics. Regular funnel analysis will help you
              identify bottlenecks and optimize your sales process.
            </p>
          </div>
        </div>
      </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default FunnelManagementDocs;
